/**
 * Los 6 chips de tipo de feedback.
 *
 * **Single-select, a diferencia del mockup**, que los hace toggle. Razón: `consulta` e `ia_bug` no
 * son "tipos", son MODOS que cambian el destino del envío (uno manda a WhatsApp, el otro despliega
 * un flujo de tres pasos). Dos prendidos a la vez son dos cards de colores distintos apiladas en
 * 380px, con tres CTAs compitiendo por el mismo click.
 *
 * Single-select además habilita el patrón de teclado que el usuario espera de un grupo de opciones
 * excluyentes: `role="radiogroup"` + roving tabindex + flechas.
 */
import React, {useRef} from 'react';
import {useTranslation} from 'react-i18next';

export const FEEDBACK_TYPES = [
    {key: 'bug', icon: 'M12 8V6M8.5 9.5L7 8M15.5 9.5L17 8M7 13H4M20 13h-3M8.5 16.5L7 18M15.5 16.5L17 18', circle: true},
    {key: 'ux', icon: 'M4 15l4-8 4 6 3-4 5 6M4 19h16'},
    {key: 'idea', icon: 'M9 18h6M10 21h4M12 3a6 6 0 0 1 4 10.5c-.8.7-1 1.5-1 2.5h-6c0-1-.2-1.8-1-2.5A6 6 0 0 1 12 3z'},
    {key: 'love', icon: 'M12 20s-7-4.5-9-9a5 5 0 0 1 9-3 5 5 0 0 1 9 3c-2 4.5-9 9-9 9z'},
    {key: 'consulta', icon: 'M9.5 9a2.5 2.5 0 1 1 3.4 2.3c-.8.3-.9 1-.9 1.7M12 16.5v.01', circle: true},
    {key: 'ia_bug', icon: 'M9 9l6 6M15 9l-6 6', circle: true},
];

const CHECK_PATH = 'M5 13l4 4L19 7';

export const FeedbackTypeChips = ({selected, onSelect, labelledBy}) => {
    const {t} = useTranslation();
    const chipRefs = useRef({});

    const focusIndex = (index) => {
        const wrapped = (index + FEEDBACK_TYPES.length) % FEEDBACK_TYPES.length;
        const type = FEEDBACK_TYPES[wrapped];
        const node = chipRefs.current[type.key];
        if (node) node.focus();
        // Las flechas mueven Y seleccionan: es el contrato de un radiogroup.
        onSelect(type.key);
    };

    const handleKeyDown = (event, index) => {
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                event.preventDefault();
                focusIndex(index + 1);
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                event.preventDefault();
                focusIndex(index - 1);
                break;
            case 'Home':
                event.preventDefault();
                focusIndex(0);
                break;
            case 'End':
                event.preventDefault();
                focusIndex(FEEDBACK_TYPES.length - 1);
                break;
            default:
                break;
        }
    };

    // Roving tabindex: sólo un chip es tabulable. Con los 6 en el orden de tabulación, salir del
    // grupo cuesta 6 tabs.
    const tabbableKey = selected || FEEDBACK_TYPES[0].key;

    return (
        <div className="feedback-chips" role="radiogroup" aria-labelledby={labelledBy}>
            {FEEDBACK_TYPES.map((type, index) => {
                const isOn = selected === type.key;
                return (
                    <button
                        key={type.key}
                        ref={(node) => {
                            chipRefs.current[type.key] = node;
                        }}
                        type="button"
                        role="radio"
                        aria-checked={isOn}
                        tabIndex={type.key === tabbableKey ? 0 : -1}
                        className={`feedback-chip ${isOn ? '-on' : ''}`}
                        // Click en el chip ya seleccionado lo deselecciona: la gente se equivoca de
                        // click y tiene que poder volver atrás. Queda fuera del contrato de teclado,
                        // donde las flechas siempre seleccionan.
                        onClick={() => onSelect(isOn ? null : type.key)}
                        onKeyDown={(event) => handleKeyDown(event, index)}
                    >
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            {/* Tres señales para el seleccionado (relleno pleno + tilde + peso), no
                                un tinte lavado: la regla de estados del design system. */}
                            {isOn ? <path d={CHECK_PATH}/> : (
                                <>
                                    {type.circle && <circle cx="12" cy="12" r="9"/>}
                                    <path d={type.icon}/>
                                </>
                            )}
                        </svg>
                        {t(`feedback.type.${type.key}`)}
                    </button>
                );
            })}
        </div>
    );
};

export default FeedbackTypeChips;
